import { Component } from "react";
import PromineoDataGrid from "components/common/controls/grid/PromineoDataGrid";
import { Column, IDataGridOptions } from "devextreme-react/data-grid";
import { connect } from "react-redux";
import { RootState, AppDispatch } from "store/store";
import AdminProduct from "interfaces/response/AdminProduct";
import Status from "components/common/Status";
import { Variant } from "shared/enums/VariantEnum";
import { EditorMode } from "shared/enums/EditorModeEnum";
import ManageProductGridActionHeader from "./ManageProductGridActionHeader";
import ManageProductGridActions from "./ManageProductGridActions";
import ProductEditorPopup from "./ProductEditorPopup";
import ProductLicenseGridPopup from "./license/index";
import { setSelectedProduct, deleteProduct } from "store/slices/ProductManagementSlice";
import { getProductLicense } from "store/slices/ProductLicenseManagementSlice";
import Loader from "components/common/controls/Loader";
import { unwrapResult } from "@reduxjs/toolkit";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";

interface Props {
  dataSource?: AdminProduct[];
  dispatch?: AppDispatch;
  selectedProduct?: AdminProduct | null;
}

interface State {
  isEditProductPopupVisible: boolean;
  isDeleteProductPopupVisible: boolean;
  isManageLicenseGridPopupVisible: boolean;
  isLoading: boolean;
  slectedRowData?: AdminProduct;
}

export class ProductsGrid extends Component<Props, State> {

  state: Readonly<State> = {
    isEditProductPopupVisible: false,
    isDeleteProductPopupVisible: false,
    isManageLicenseGridPopupVisible: false,
    isLoading: false,
    slectedRowData: undefined,
  };

  StatusCellRender = ({ data }: { data: AdminProduct }) => {
    return (
      <Status 
        variant={data.isActive ? Variant.Success : Variant.Danger} 
        text={data.isActive ? "Active" : "Inactive"} 
      />
    );
  };

  handleEditProductClick = (data: AdminProduct) => {
    this.setState({
      isEditProductPopupVisible: true,
      slectedRowData: data,
    });
  };

  handleEditProductPopupClose = () => {
    this.setState({
      isEditProductPopupVisible: false,
    });
  };

  handleDeleteProductClick = (data: AdminProduct) => {
    this.setState({
      isDeleteProductPopupVisible: true,
      slectedRowData: data,
    });
  };

  handleDeleteProductPopupClose = () => {
    this.setState({
      isDeleteProductPopupVisible: false,
    });
  };

  handleDeleteConfirm = () => {
    this.setState({ isLoading: true });
    this.props.dispatch!(deleteProduct({productId: this.state.slectedRowData!.id}))
      .then(() => {
        this.setState({ isDeleteProductPopupVisible: false, isLoading: false });
      });
  }

  handleManageLicenseClick = (data: AdminProduct) => {
    this.setState({isLoading: true});
    this.props.dispatch!(setSelectedProduct(data));
    this.props.dispatch!(getProductLicense(data.id))
      .then(unwrapResult)
      .then(() => {
        this.setState({ isManageLicenseGridPopupVisible: true });
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  handleManageLicensePopupClose = () => {
    this.setState({
      isManageLicenseGridPopupVisible: false,
    });
  };
  
  render() {
    const dataGridOptions: IDataGridOptions = {
      dataSource: this.props.dataSource,
      keyExpr: "id",
    };

    return (
      <>
        <div className="mb-20">
          <ManageProductGridActionHeader />
        </div>
        <div>
          <PromineoDataGrid
            dataGridOptions={dataGridOptions}
            classNames="max-h-600px"
            noDataText="No Products found.
            (Click ‘Add Product’ to create new product)"
          >
            <Column
              dataField="name"
              caption="Name"
              width="10%"
            />
            <Column
              dataField="code"
              caption="Code"
              width="6%"
            />
            <Column
              dataField="description"
              caption="Description"
              width="30%"
            />
            <Column
              caption="Status"
              dataField="status"
              cellRender={this.StatusCellRender}
              width="8%"
              alignment="center"
            />
            <Column
              dataField="stripeId"
              caption="Stripe Id"
              width="10%"
            />
            <Column
              dataField="actionPricePer1000"
              caption="Price Per 1000 Action"
              width="10%"
            />
            <Column
              dataField="isDefaultProduct"
              caption="Default Product"
              width="15%"
            />
            <Column
              caption="Actions"
              cssClass="w-16 lg:w-48 border"
              cellRender={(productData: any) => (
                <ManageProductGridActions
                  onEditClick={() => {
                    this.handleEditProductClick(productData.data)
                  }}
                  onDeleteClick={() => {
                    this.handleDeleteProductClick(productData.data)
                  }}
                  onManageLicenseClick={() => {
                    this.handleManageLicenseClick(productData.data)
                  }} 
                />
              )}
            />
          </PromineoDataGrid>
        </div>
        <Loader isVisible={this.state.isLoading} />
        {this.state.isEditProductPopupVisible && (
          <ProductEditorPopup
            onClose={this.handleEditProductPopupClose}
            mode={EditorMode.Edit}
            product={this.state.slectedRowData}
          />
        )}
        {this.state.isManageLicenseGridPopupVisible && (
          <ProductLicenseGridPopup
            onClose={this.handleManageLicensePopupClose}
          />
        )}
        {this.state.isDeleteProductPopupVisible && (
          <DeleteConfirmationDialog
            handleConfirm={this.handleDeleteConfirm}
            onClose={this.handleDeleteProductPopupClose}
            content={"Do you want to delete this product?"}
          />
        )}
      </>
    );
  }
}

const mapStateToProps = (state: RootState, props: Props) => {
  return {
    dataSource: state.productManagementData.products,
    selectedProduct: state.productManagementData.selectedProduct,
    ...props,
  };
};

export default connect(mapStateToProps)(ProductsGrid);